import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ThemeSwitchComponent } from '@components/user-profile-dropdown/theme-switch/theme-switch.component';
import { IconoirLogOut, IconoirNavArrowDown, IconoirPrivacyPolicy } from '@ey-xd/motif-icon';
import { MotifAvatarModule, MotifButtonModule, MotifIconModule } from '@ey-xd/ng-motif';
import { APP_EVENT_KEY } from '@models/common.models';
import { TranslatePipe } from '@ngx-translate/core';
import { EventService, MainHelperService } from '@services';
import { EytcAuthService } from '@services/authentication/eytc-auth.service';
import { UserInfoComponent } from './user-info/user-info.component';

@Component({
  selector: 'app-user-profile-dropdown',
  standalone: true,
  imports: [
    MotifAvatarModule,
    MotifIconModule,
    ThemeSwitchComponent,
    UserInfoComponent,
    TranslatePipe,
    MotifButtonModule,
  ],
  templateUrl: './user-profile-dropdown.component.html',
  styleUrl: './user-profile-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileDropdownComponent implements OnDestroy, AfterViewInit {
  public showMenu = signal(false);
  public readonly dropdownItems = [
    {
      title: 'common.header.user-profile.privacy-notice',
      icon: IconoirPrivacyPolicy,
      onClick: () => this.#showPrivacyPolicy(),
    },
    {
      title: 'common.header.user-profile.log-out',
      icon: IconoirLogOut,
      onClick: () => this.#eventService.emitEvent(APP_EVENT_KEY.LOGOUT),
    },
  ];

  @ViewChild('userMenu') userMenu!: ElementRef;

  #documentClickListener: (() => void) | undefined;
  #authService = inject(EytcAuthService);
  #eventService = inject(EventService);
  #helper: MainHelperService = inject(MainHelperService);
  #destroyRef: DestroyRef = inject(DestroyRef);
  #renderer: Renderer2 = inject(Renderer2);

  public get userProfilePresent() {
    return this.#authService.userProfile() !== null;
  }

  public get fullName() {
    return this.#authService.userProfile()?.fullName ?? '';
  }

  #privacyNoticeShown = signal<boolean>(false);
  #showPrivacyPolicy(): void {
    // TODO this should be shown in modal, without the need of acking the PN

    if (this.#privacyNoticeShown()) {
      return;
    }

    this.#privacyNoticeShown.set(true);

    this.#helper
      .checkPrivacyNotice(true, 'show')
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.#privacyNoticeShown.set(false);
      });
  }

  toggleDropdown() {
    this.showMenu.set(!this.showMenu());
  }

  protected readonly arrowDownIcon = IconoirNavArrowDown;

  ngAfterViewInit() {
    this.#documentClickListener = this.#renderer.listen('document', 'click', (event: MouseEvent) => {
      if (this.userMenu && event.target instanceof Element && !this.userMenu.nativeElement.contains(event.target)) {
        this.showMenu.set(false);
      }
    });
  }

  ngOnDestroy() {
    if (this.#documentClickListener) {
      this.#documentClickListener();
    }
  }
}
